import HybridBridge, { HybridBridgeLJ } from '../miniprogram/hybridBridge';
import { registerGlobalValue, cache } from '../utils/registerGlobalValue';
import { DeviceInfo } from '../typings/DeviceInfo';
import { JSBridgeReturnType } from '../typings/JSBridgeProtocol';
import { BaseController } from './BaseController';
import { StaticData } from '../typings/StaticData';

/**
 * Android WebView 控件。
 */
export class WxMiniprogramController extends BaseController {
  private _hybridBridge: HybridBridge = new HybridBridge();
  deviceInfo?: DeviceInfo;
  staticData?: StaticData;
  initializing = false;

  get hybridBridge() {
    return this._hybridBridge
  }
  
  private async checkBridgeReady(timeout = 60000): Promise<boolean> {
    if (this.ready) return true;

    if (this.initializing) {
      const start = performance.now();
      return await new Promise<boolean>((resolve, reject) => {
        const check = () => {
          if (this.ready) return resolve(true);
          if (performance.now() - start > timeout) {
            return reject(new Error('HybridBridge 初始化超时~'));
          }
          requestAnimationFrame(check);
        };
        check();
      });
    }

    this.initializing = true;
    try {
      const [res, error] = await this._hybridBridge.initialize(timeout);
      if (!res || error) throw error || new Error('HybridBridge 初始化失败~');

      return await new Promise<boolean>((resolve, reject) => {
        const timer = setTimeout(() => reject(new Error('getStaticData 超时~')), timeout);
        this._hybridBridge.getStaticData(
          registerGlobalValue(
            'HYBRID_BRIDGE_NAME',
            (data: StaticData | string) => {
              clearTimeout(timer);
              this.staticData = (() => {
                try {
                  return JSON.parse(data as string) as StaticData;
                } catch (e) {
                  return data as StaticData;
                }
              })();
              this.deviceInfo = this.staticData;
              resolve(true);
            },
          ),
        );
      });
    } finally {
      this.initializing = false;
    }
  }

  async checkReady(timeout = 60000) {
    const res: JSBridgeReturnType<boolean> = [null, null];

    if (this.ready) {
      res[0] = true;
      return res;
    }

    try {
      res[0] = await this.checkBridgeReady(timeout);
    } catch (error) {
      res[0] = false;
      res[1] = error as Error;
    }

    return res;
  }

  async getStaticData(timeout = 60000): Promise<JSBridgeReturnType<StaticData | false>> {
    const [ready, readyError] = await this.checkReady(timeout);
    if (!ready) return [false, readyError];
    return [this.staticData || false, null];
  }

  get ready() {
    return !!this._hybridBridge.isReady && !!this.staticData;
  }

  get scheme() {
    return this.deviceInfo?.scheme || '';
  }

  createSchemeLink(schemeUrl: string) {
    if (schemeUrl.startsWith(this.scheme)) {
      return schemeUrl;
    }
    return this.scheme + '://' + schemeUrl;
  }

  async callAndBackfeed<T>(scheme: string) {
    const res = [false, null] as JSBridgeReturnType<T | false>;
    const [ready, readyError] = await this.checkReady();

    if (!ready) {
      res[0] = false;
      res[1] = readyError;

      return res;
    }

    return await new Promise<JSBridgeReturnType<T | false>>(async (resolve) => {
      const actionUrl = this.createSchemeLink(scheme);

      const msg = JSON.stringify({
        actionUrl,
        functionName: registerGlobalValue(
          this.createSchemeLink(scheme),
          (data: string) => {
            let d: any = data;
            try {
              d = JSON.parse(data);
            } catch (e) {
              /** no-care */
            }
            res[0] = (() => {
              if (d === 1) return true;
              if (d === 0) return false;
              return d;
            })() as T;

            resolve(res);
          },
        ),
      });

      this._hybridBridge.callAndBackfeed(msg);
    });
  }

  async callAndListen<T>(scheme: string, callback: (data: T) => void) {
    const res = [false, null] as JSBridgeReturnType<false | (() => void)>;
    const [ready, readyError] = await this.checkReady();

    if (!ready) {
      res[0] = false;
      res[1] = readyError;

      return res;
    }

    return await new Promise<JSBridgeReturnType<false | (() => void)>>(
      async (resolve) => {
        const actionUrl = this.createSchemeLink(scheme);
        const _callback = (data: string) => {
          let d: any = data;
          try {
            d = JSON.parse(data);
          } catch (e) {
            /** no-care */
          }

          const callbackParam = (() => {
            if (d === 1) return true;
            if (d === 0) return false;
            return d;
          })() as T;

          callback(callbackParam);
          res[0] = () => {
            // 清理掉当前的回调引用
            const callbacks = cache[actionUrl];
            if (!callbacks) return;

            const idx = callbacks.queue.findIndex(
              (func) => func.callback === _callback,
            );
            if (idx !== -1) {
              callbacks.queue.splice(idx, 1);
            }
          };
          resolve(res);
        };

        const msg = JSON.stringify({
          actionUrl,
          functionName: registerGlobalValue(actionUrl, _callback, false),
        });

        this._hybridBridge.callAndListen(msg);
      },
    );
  }

  async invoke<P, R>(method: string, data?: P) {
    const res = [false, null] as JSBridgeReturnType<false | (() => void)>;
    const [ready, readyError] = await this.checkReady();

    if (!ready) {
      res[0] = false;
      res[1] = readyError;

      throw readyError;
    } else {
      return (await this._hybridBridge.invoke?.(method, data)) as R;
    }
  }
}
